import { supabase } from '../supabase';
import type { Database } from '../database.types';

type Message = Database['public']['Tables']['messages']['Row'];
type MessageInsert = Database['public']['Tables']['messages']['Insert'];
type Conversation = Database['public']['Tables']['conversations']['Row'];

export interface ConversationWithMessages extends Conversation {
  messages: Message[];
  participants: {
    user_id: string;
    full_name: string;
    avatar_url: string | null;
  }[];
  property?: {
    id: string;
    title: string;
    photos: any;
    owner_id?: string;
  };
  client?: {
    id: string;
    name: string;
    email: string;
    photo_url: string | null;
  };
  agent?: {
    id: string;
    agency_name: string | null;
  };
}

export interface MessageWithSender extends Message {
  sender: {
    full_name: string;
    avatar_url: string | null;
  };
}

export class MessagingAPI {
  // Get conversations for an agent
  static async getAgentConversations(agentId: string): Promise<ConversationWithMessages[]> {
    const { data, error } = await supabase
      .from('conversations')
      .select(`
        *,
        messages(
          id,
          content,
          sender_id,
          read_at,
          created_at
        ),
        property:properties(
          id,
          title,
          photos
        ),
        client:clients(
          id,
          name,
          email,
          photo_url
        ),
        agent:agents(
          id,
          agency_name
        )
      `)
      .eq('agent_id', agentId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching agent conversations:', error);
      throw error;
    }

    // Enrich messages with sender information
    const conversationsWithSenders = await Promise.all(
      (data || []).map(async (conversation) => {
        const enrichedMessages = await Promise.all(
          (conversation.messages || []).map(async (message: any) => {
            const { data: sender } = await supabase
              .from('users')
              .select('full_name, avatar_url')
              .eq('id', message.sender_id)
              .single();

            return {
              ...message,
              sender: {
                full_name: sender?.full_name || 'Unknown',
                avatar_url: sender?.avatar_url
              }
            };
          })
        );

        return {
          ...conversation,
          messages: enrichedMessages
        };
      })
    );

    return conversationsWithSenders;
  }

  // Get conversations for a landlord (both as agent and property owner)
  static async getLandlordConversations(landlordUserId: string): Promise<ConversationWithMessages[]> {
    console.log(`🔍 MessagingAPI: Getting conversations for landlord ${landlordUserId}`);
    
    try {
      // Get conversations where user is the agent
      const { data: agentConversations, error: agentError } = await supabase
        .from('conversations')
        .select(`
          *,
          messages(
            id,
            content,
            sender_id,
            read_at,
            created_at
          ),
          property:properties(
            id,
            title,
            photos,
            owner_id
          ),
          client:clients(
            id,
            name,
            email,
            photo_url
          ),
          agent:agents(
            id,
            agency_name
          )
        `)
        .eq('agent_id', landlordUserId)
        .order('created_at', { ascending: false });

      if (agentError) {
        console.error('🚨 Error fetching agent conversations:', agentError);
      }

      // Get conversations about properties the user owns
      const { data: propertyConversations, error: propError } = await supabase
        .from('conversations')
        .select(`
          *,
          messages(
            id,
            content,
            sender_id,
            read_at,
            created_at
          ),
          property:properties!inner(
            id,
            title,
            photos,
            owner_id
          ),
          client:clients(
            id,
            name,
            email,
            photo_url
          ),
          agent:agents(
            id,
            agency_name
          )
        `)
        .eq('property.owner_id', landlordUserId)
        .order('created_at', { ascending: false });

      if (propError) {
        console.error('🚨 Error fetching property conversations:', propError);
      }

      // Combine both sets of conversations
      const allConversations = [
        ...(agentConversations || []),
        ...(propertyConversations || [])
      ];

      // Remove duplicates based on conversation ID
      const uniqueConversations = allConversations.filter((conv, index, self) => 
        index === self.findIndex(c => c.id === conv.id)
      );

      console.log(`📊 MessagingAPI: Found ${agentConversations?.length || 0} agent conversations`);
      console.log(`📊 MessagingAPI: Found ${propertyConversations?.length || 0} property conversations`);
      console.log(`📊 MessagingAPI: Total unique conversations: ${uniqueConversations.length}`);

      // Enrich messages with sender information
      const conversationsWithSenders = await Promise.all(
        uniqueConversations.map(async (conversation) => {
          const enrichedMessages = await Promise.all(
            (conversation.messages || []).map(async (message: any) => {
              const { data: sender } = await supabase
                .from('users')
                .select('full_name, avatar_url')
                .eq('id', message.sender_id)
                .single();

              return {
                ...message,
                sender: {
                  full_name: sender?.full_name || 'Unknown',
                  avatar_url: sender?.avatar_url
                }
              };
            })
          );

          return {
            ...conversation,
            messages: enrichedMessages
          };
        })
      );

      console.log(`✅ MessagingAPI: Loaded ${conversationsWithSenders.length} conversations for landlord ${landlordUserId}`);
      return conversationsWithSenders;
    } catch (error) {
      console.error('🚨 MessagingAPI: Error in getLandlordConversations:', error);
      throw error;
    }
  }

  // Get conversations for a client
  static async getClientConversations(clientId: string): Promise<ConversationWithMessages[]> {
    const { data, error } = await supabase
      .from('conversations')
      .select(`
        *,
        messages(
          id,
          content,
          sender_id,
          read_at,
          created_at
        ),
        property:properties(
          id,
          title,
          photos
        ),
        client:clients(
          id,
          name,
          email,
          photo_url
        ),
        agent:agents(
          id,
          agency_name
        )
      `)
      .eq('client_id', clientId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching client conversations:', error);
      throw error;
    }

    // Enrich messages with sender information
    const conversationsWithSenders = await Promise.all(
      (data || []).map(async (conversation) => {
        const enrichedMessages = await Promise.all(
          (conversation.messages || []).map(async (message: any) => {
            const { data: sender } = await supabase
              .from('users')
              .select('full_name, avatar_url')
              .eq('id', message.sender_id)
              .single();

            return {
              ...message,
              sender: {
                full_name: sender?.full_name || 'Unknown',
                avatar_url: sender?.avatar_url
              }
            };
          })
        );

        return {
          ...conversation,
          messages: enrichedMessages
        };
      })
    );

    return conversationsWithSenders;
  }

  // Get a specific conversation with messages
  static async getConversation(conversationId: string): Promise<ConversationWithMessages | null> {
    const { data, error } = await supabase
      .from('conversations')
      .select(`
        *,
        messages(
          id,
          content,
          sender_id,
          read_at,
          created_at
        ),
        property:properties(
          id,
          title,
          photos
        ),
        client:clients(
          id,
          name,
          email,
          photo_url
        ),
        agent:agents(
          id,
          agency_name
        )
      `)
      .eq('id', conversationId)
      .single();

    if (error) {
      console.error('Error fetching conversation:', error);
      throw error;
    }

    if (!data) return null;

    // Enrich messages with sender information
    const enrichedMessages = await Promise.all(
      (data.messages || []).map(async (message: any) => {
        const { data: sender } = await supabase
          .from('users')
          .select('full_name, avatar_url')
          .eq('id', message.sender_id)
          .single();

        return {
          ...message,
          sender: {
            full_name: sender?.full_name || 'Unknown',
            avatar_url: sender?.avatar_url
          }
        };
      })
    );

    return {
      ...data,
      messages: enrichedMessages
    };
  }

  // Send a message
  static async sendMessage(messageData: MessageInsert): Promise<Message> {
    const { data, error } = await supabase
      .from('messages')
      .insert(messageData)
      .select()
      .single();

    if (error) {
      console.error('Error sending message:', error);
      throw error;
    }

    return data;
  }

  // Mark message as read
  static async markMessageAsRead(messageId: string): Promise<void> {
    const { error } = await supabase
      .from('messages')
      .update({ read_at: new Date().toISOString() })
      .eq('id', messageId);

    if (error) {
      console.error('Error marking message as read:', error);
      throw error;
    }
  }

  // Mark all messages in conversation as read
  static async markConversationAsRead(conversationId: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('messages')
      .update({ read_at: new Date().toISOString() })
      .eq('conversation_id', conversationId)
      .neq('sender_id', userId);

    if (error) {
      console.error('Error marking conversation as read:', error);
      throw error;
    }
  }

  // Create a new conversation
  static async createConversation(conversationData: {
    property_id?: string;
    agent_id?: string;
    client_id?: string;
    type: 'property' | 'client';
  }): Promise<Conversation> {
    const { data, error } = await supabase
      .from('conversations')
      .insert(conversationData)
      .select()
      .single();

    if (error) {
      console.error('Error creating conversation:', error);
      throw error;
    }

    return data;
  }

  // Get unread message count for an agent
  static async getUnreadMessageCount(agentId: string): Promise<number> {
    const { count, error } = await supabase
      .from('messages')
      .select('*', { count: 'exact', head: true })
      .eq('conversations.agent_id', agentId)
      .is('read_at', null)
      .neq('sender_id', agentId);

    if (error) {
      console.error('Error getting unread message count:', error);
      throw error;
    }

    return count || 0;
  }

  // Get unread message count for a client
  static async getClientUnreadMessageCount(clientId: string): Promise<number> {
    const { count, error } = await supabase
      .from('messages')
      .select('*', { count: 'exact', head: true })
      .eq('conversations.client_id', clientId)
      .is('read_at', null)
      .neq('sender_id', clientId);

    if (error) {
      console.error('Error getting client unread message count:', error);
      throw error;
    }

    return count || 0;
  }

  // Subscribe to real-time messages
  static subscribeToMessages(conversationId: string, callback: (message: Message) => void) {
    return supabase
      .channel(`messages:${conversationId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`
        },
        (payload) => {
          callback(payload.new as Message);
        }
      )
      .subscribe();
  }

  // Subscribe to conversation updates
  static subscribeToConversations(userId: string, callback: (conversation: Conversation) => void) {
    return supabase
      .channel(`conversations:${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'conversations'
        },
        (payload) => {
          callback(payload.new as Conversation);
        }
      )
      .subscribe();
  }
}
